import { sdk } from '@needful-things-mvp/sdk';

import { hoursSince } from './api-format';
import { USE_MOCK } from './data-mode';
import { unwrap } from './http';
import { type Paginated, delay, paginate } from './mock';

export interface VoterEmail {
  id: string;
  email: string;
  project: string;
  projectSlug: string;
  artist: string;
  artistSlug: string;
  date: string;
  updatedHours: number;
}

export interface ListVoterEmailsParams {
  search?: string;
  page?: number;
  pageSize?: number;
}

export type PaginatedVoterEmails = Paginated<VoterEmail>;

export const VOTER_EMAIL_PAGE_SIZE = 8;
export const VOTER_EMAIL_PAGE_SIZE_OPTIONS = [8, 16, 24, 48];

const db: VoterEmail[] = [
  {
    id: 've-1',
    email: 'ana.souza@example.com',
    project: 'Bloom',
    projectSlug: 'bloom',
    artist: 'Mara Oliveira',
    artistSlug: 'mara-oliveira',
    date: '06 Oct 2026',
    updatedHours: 2,
  },
  {
    id: 've-2',
    email: 'bruno.lima@example.com',
    project: 'Bloom',
    projectSlug: 'bloom',
    artist: 'Tomas Beck',
    artistSlug: 'tomas-beck',
    date: '06 Oct 2026',
    updatedHours: 4,
  },
  {
    id: 've-3',
    email: 'carla.dias@example.com',
    project: 'Bloom',
    projectSlug: 'bloom',
    artist: 'Mara Oliveira',
    artistSlug: 'mara-oliveira',
    date: '07 Oct 2026',
    updatedHours: 10,
  },
  {
    id: 've-4',
    email: 'diego.rocha@example.com',
    project: 'Salt & Iron',
    projectSlug: 'salt-and-iron',
    artist: 'Ade Bakare',
    artistSlug: 'ade-bakare',
    date: '11 Oct 2026',
    updatedHours: 20,
  },
  {
    id: 've-5',
    email: 'elisa.prado@example.com',
    project: 'Salt & Iron',
    projectSlug: 'salt-and-iron',
    artist: 'Ade Bakare',
    artistSlug: 'ade-bakare',
    date: '12 Oct 2026',
    updatedHours: 28,
  },
  {
    id: 've-6',
    email: 'felipe.nunes@example.com',
    project: 'Ember Field',
    projectSlug: 'ember-field',
    artist: 'Lena Costa',
    artistSlug: 'lena-costa',
    date: '18 Oct 2026',
    updatedHours: 40,
  },
  {
    id: 've-7',
    email: 'gina.park@example.com',
    project: 'Ember Field',
    projectSlug: 'ember-field',
    artist: 'Lena Costa',
    artistSlug: 'lena-costa',
    date: '19 Oct 2026',
    updatedHours: 52,
  },
  {
    id: 've-8',
    email: 'hugo.meyer@example.com',
    project: 'Bloom',
    projectSlug: 'bloom',
    artist: 'Priya Nair',
    artistSlug: 'priya-nair',
    date: '20 Oct 2026',
    updatedHours: 64,
  },
  {
    id: 've-9',
    email: 'iris.cole@example.com',
    project: 'Paper Cities',
    projectSlug: 'paper-cities',
    artist: 'Yuki Sato',
    artistSlug: 'yuki-sato',
    date: '22 Oct 2026',
    updatedHours: 80,
  },
  {
    id: 've-10',
    email: 'jonas.vidal@example.com',
    project: 'Paper Cities',
    projectSlug: 'paper-cities',
    artist: 'Yuki Sato',
    artistSlug: 'yuki-sato',
    date: '23 Oct 2026',
    updatedHours: 96,
  },
];

/** Wire shape from `GET /admin/voter-emails` (refs resolved by the API). */
interface VoterEmailDto {
  id: string;
  email: string;
  project: string | null;
  projectSlug: string | null;
  artist: string | null;
  artistSlug: string | null;
  createdAt: string;
}

function mapVoterEmail(dto: VoterEmailDto): VoterEmail {
  return {
    id: dto.id,
    email: dto.email,
    project: dto.project ?? '',
    projectSlug: dto.projectSlug ?? '',
    artist: dto.artist ?? '',
    artistSlug: dto.artistSlug ?? '',
    date: new Date(dto.createdAt).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    updatedHours: hoursSince(dto.createdAt),
  };
}

export async function listVoterEmails(
  params: ListVoterEmailsParams = {},
): Promise<PaginatedVoterEmails> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin['voter-emails'].get({
      query: { search: params.search, page: params.page, pageSize: params.pageSize },
    });
    const page = unwrap<Paginated<VoterEmailDto>>(response);
    return { ...page, items: page.items.map(mapVoterEmail) };
  }

  await delay();
  const { search, page, pageSize = VOTER_EMAIL_PAGE_SIZE } = params;

  const term = search?.trim().toLowerCase();
  const filtered = term
    ? db.filter(
        (row) =>
          row.email.toLowerCase().includes(term) ||
          row.project.toLowerCase().includes(term) ||
          row.artist.toLowerCase().includes(term),
      )
    : db;

  const sorted = [...filtered].sort((a, b) => a.updatedHours - b.updatedHours);
  return paginate(sorted, page, pageSize);
}

export function queryVoterEmails(
  params: ListVoterEmailsParams = {},
): Promise<PaginatedVoterEmails> {
  return listVoterEmails(params);
}
